import React, { useContext } from 'react';
import GameAssetsContext from '../../../../../context/GameAssetContext';
import { championImages } from '../ComboDisplay/Events/Images/championImages';

// Helper function to extract base ability from duration-enhanced sequences
const extractBaseAbility = (skill: string): string => {
  // Handle wait/delay notation (_:1.5 or just _)
  if (skill.startsWith('_')) {
    return '_';
  }
  
  // Handle cast time break (T:0.5)
  if (skill.startsWith('T:')) {
    return 'T';
  }
  
  // Handle blocking hold notation (E[3], Q[max], R[min])
  const blockingHoldMatch = skill.match(/^([QWERA])\[/);
  if (blockingHoldMatch) {
    return blockingHoldMatch[1];
  }
  
  // Handle non-blocking toggle notation (R::4, Q::max)
  const nonBlockingToggleMatch = skill.match(/^([QWERA])::/);
  if (nonBlockingToggleMatch) {
    return nonBlockingToggleMatch[1];
  }
  
  // Handle explicit recast format (E:3E, Q:2Q, etc.) — legacy
  const explicitRecastMatch = skill.match(/^([QWERA]):(\d+\.?\d*|\w+)\1$/);
  if (explicitRecastMatch) {
    return explicitRecastMatch[1];
  }
  
  // Handle duration format (E:3, Q:max, E:min, etc.) — legacy
  if (skill.includes(':')) {
    const [ability] = skill.split(':');
    if (['Q', 'W', 'E', 'R', 'A'].includes(ability)) {
      return ability;
    }
  }
  
  // Return as-is if no duration syntax found
  return skill;
};

// Helper: extract KeyPress events from logs in sequence order
const extractKeyPressEventsFromLogs = (events: any[]): Array<{ability: string, imageState: string, championName: string}> => {
  if (!events || events.length === 0) return [];
  
  const keyPressEvents: Array<{ability: string, imageState: string, championName: string}> = [];
  
  const traverseEvents = (eventList: any[]) => {
    eventList.forEach(event => {
      if (event.type === 'KeyPress') {
        const abilityKey = event.details?.abilityKey?.toUpperCase() || '';
        const imageState = event.imageState || 'default';
        const champName = event.details?.championName || '';
        
        if (abilityKey) {
          keyPressEvents.push({ ability: abilityKey, imageState, championName: champName });
        }
      }
      
      if (event.childEvents && event.childEvents.length > 0) {
        traverseEvents(event.childEvents);
      }
    });
  };
  
  traverseEvents(events);
  return keyPressEvents;
};

interface MiniAbilityIconsProps {
  sequence: string;
  championName: string;
  events?: any[];
}

const MiniAbilityIcons: React.FC<MiniAbilityIconsProps> = ({ sequence, championName, events }) => {
  const { championsAssets } = useContext(GameAssetsContext);
  
  // Try to extract KeyPress events from logs first
  const keyPressEvents = events ? extractKeyPressEventsFromLogs(events) : [];
  
  // Fallback: extract individual abilities from sequence
  const fallbackAbilities = sequence
    .split(/[,\s]/)
    .filter(Boolean)
    .flatMap(token => {
      if (token.includes('>')) {
        return token.split('>').filter(p => p.trim() !== '');
      }
      return [token];
    })
    .map(ability => {
      const trimmed = ability.trim().toUpperCase();
      return extractBaseAbility(trimmed);
    })
    .filter(ability => ability !== '_' && ability !== 'T');

  // Return if assets aren't loaded yet
  if (!championsAssets[championName]) {
    return <span className="mini-sequence-text">{sequence}</span>;
  }
  
  const spellImageUrls = championsAssets[championName].spellImageUrls;
  const autoAttackImageUrl = championsAssets[championName].autoAttackImageUrl;
  
  // Use KeyPress events if available, otherwise fallback to sequence parsing
  const abilitiesToRender = keyPressEvents.length > 0 ? keyPressEvents : fallbackAbilities.map(ability => ({
    ability,
    imageState: 'default',
    championName
  }));
  
  return (
    <div className="mini-ability-sequence">
      {abilitiesToRender.map((abilityData, idx) => {
        const ability = typeof abilityData === 'string' ? abilityData : abilityData.ability;
        const imageState = typeof abilityData === 'string' ? 'default' : abilityData.imageState;
        let iconUrl = '';
        
        // For abilities (not auto attacks), try championImages with imageState first
        if (ability !== 'A' && championName && championImages[championName]) {
          const champMap = championImages[championName];
          
          // 1) If imageState != "default", try championImages[championName][imageState]
          if (imageState && imageState !== "default") {
            iconUrl = champMap[imageState.toLowerCase()];
          }
          
          // 2) Fallback to champMap[abilityKey]
          if (!iconUrl) {
            iconUrl = champMap[ability.toLowerCase()];
          }
        }
        
        // Fallback to base spell images
        if (!iconUrl) {
          switch (ability) {
            case 'Q':
              iconUrl = spellImageUrls.Q;
              break;
            case 'W':
              iconUrl = spellImageUrls.W;
              break;
            case 'E':
              iconUrl = spellImageUrls.E;
              break;
            case 'R':
              iconUrl = spellImageUrls.R;
              break;
            case 'A':
              iconUrl = autoAttackImageUrl;
              break;
            default:
              return <span key={`ability-${idx}`} className="unknown-ability">{ability}</span>;
          }
        }
        
        return (
          <React.Fragment key={`ability-fragment-${idx}`}>
            <div className="mini-ability-icon">
              <img src={iconUrl} alt={ability} />
              <span className="mini-ability-letter">{ability}</span>
            </div>
            {/* Add arrow between icons, but not after the last one */}
            {idx < abilitiesToRender.length - 1 && <span className="ability-arrow">→</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default MiniAbilityIcons;
